import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const CONTENTFUL_GRAPHQL_URL = import.meta.env.VITE_CONTENTFUL_GRAPHQL_URL;
const CONTENTFUL_ACCESS_TOKEN = import.meta.env.VITE_CONTENTFUL_ACCESS_TOKEN;

interface ContentfulResponse {
  data: {
    jsonContent: {
      sys: {
        id: string;
      };
      id: string;
      json: Record<string, string>;
    };
  };
}

interface UseGetLabelsParams {
  id: string;
  locale?: string;
  enabled?: boolean;
}

/**
 * Custom hook to fetch labels from Contentful using GraphQL API
 * Falls back to US English labels if the requested locale is not available
 *
 * @param id - The Contentful entry ID
 * @param locale - The locale to fetch labels for (default: 'en-US')
 * @param enabled - Whether the query should run (default: true)
 *
 * @example
 * ```tsx
 * const { data: labels, isLoading, error } = useGetLabels({
 *   id: '60n6cJSqCElDfYLQN5Sczy',
 *   locale: 'de-DE',
 * });
 * ```
 */
export const useGetLabels = ({ id, locale = 'en-US', enabled = true }: UseGetLabelsParams) => {
  return useQuery({
    queryKey: ['contentful-labels', id, locale],
    queryFn: async () => {
      const fetchLabels = async (targetLocale: string) => {
        const response = await axios.post<ContentfulResponse>(
          CONTENTFUL_GRAPHQL_URL,
          {
            query: `query ($id: String!, $locale: String!) { 
              jsonContent(id: $id, locale: $locale) { 
                sys { id } 
                id 
                json 
              } 
            }`,
            variables: {
              id,
              locale: targetLocale,
            },
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
            },
          }
        );

        return response.data.data.jsonContent;
      };

      try {
        const result = await fetchLabels(locale);

        // If the result is null or doesn't have json, fallback to en-US
        if (!result || !result.json) {
          if (locale !== 'en-US') {
            console.warn(`Labels not available for locale ${locale}, falling back to en-US`);
            const fallbackResult = await fetchLabels('en-US');
            return fallbackResult?.json || {};
          }
          return {};
        }

        return result.json;
      } catch (error) {
        // If the requested locale fails, fallback to US English
        if (locale !== 'en-US') {
          console.warn(`Failed to fetch labels for locale ${locale}, falling back to en-US`);
          try {
            const fallbackResult = await fetchLabels('en-US');
            return fallbackResult?.json || {};
          } catch (fallbackError) {
            console.error('Failed to fetch fallback labels:', fallbackError);
            throw error;
          }
        }
        throw error;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: false, // Disable retry since we handle fallback internally
  });
};
